import { useState, useEffect } from 'react';
import { stripeStatus, stripeConnectUrl, getSettings, saveSettings } from '../api';

const STEPS = [
  { id: 'stripe',   icon: '💳', title: 'Connect Payment Processing', desc: 'Accept card payments from parents via Stripe.' },
  { id: 'email',    icon: '📧', title: 'Configure Email Settings',    desc: 'Set your from address so emails come from you.' },
  { id: 'project',  icon: '📁', title: 'Create Your First Project',   desc: 'A project is a season or team.' },
  { id: 'done',     icon: '🎉', title: 'You\'re all set!',            desc: 'Add players and start collecting.' },
];

export default function OnboardingWizard({ onComplete, onSkip }) {
  const [step, setStep] = useState(0);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [emailForm, setEmailForm] = useState({ frontendUrl: 'https://app.duestrack.com', fromEmail: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([stripeStatus(), getSettings()]).then(([stripe, settings]) => {
      setStripeConnected(stripe.connected);
      setEmailForm(f => ({ ...f, fromEmail: settings.fromEmail || '', frontendUrl: settings.frontendUrl || 'https://app.duestrack.com' }));
    }).catch(() => {}).finally(() => setLoading(false));

    // Check if returning from Stripe onboarding
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') {
      setStripeConnected(true);
      setStep(1);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleStripeConnect = async () => {
    setConnecting(true);
    try {
      const res = await stripeConnectUrl();
      window.location.href = res.url;
    } catch (err) { setError(err.message); setConnecting(false); }
  };

  const handleEmailSave = async () => {
    setSavingEmail(true);
    try {
      await saveSettings(emailForm);
      setStep(2);
    } catch (err) { setError(err.message); }
    finally { setSavingEmail(false); }
  };

  const progress = Math.round((step / (STEPS.length - 1)) * 100);

  if (loading) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 620 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>💰</div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--text)', marginBottom: 6 }}>Welcome to DuesTrack</h1>
          <p style={{ color: 'var(--muted)', fontSize: 15 }}>Let's get you set up in just a few minutes.</p>
        </div>

        {/* Progress bar */}
        <div style={{ background: 'var(--border)', borderRadius: 6, height: 6, marginBottom: 32, overflow: 'hidden' }}>
          <div style={{ background: 'var(--navy)', width: `${progress}%`, height: '100%', borderRadius: 6, transition: 'width 0.4s' }} />
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', margin: '0 auto 6px',
                background: i < step ? 'var(--green)' : i === step ? 'var(--navy)' : 'var(--surface-2)',
                border: `2px solid ${i < step ? 'var(--green)' : i === step ? 'var(--navy)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, color: i <= step ? 'white' : 'var(--muted)', fontWeight: 700,
                transition: 'all 0.3s',
              }}>
                {i < step ? '✓' : i + 1}
              </div>
              <div style={{ fontSize: 10, color: i === step ? 'var(--text)' : 'var(--muted)', fontWeight: i === step ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {s.title.split(' ').slice(0, 2).join(' ')}
              </div>
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="card" style={{ padding: 32, marginBottom: 16 }}>
          {error && <div className="alert alert-error" style={{ marginBottom: 20 }}>{error}</div>}

          {/* Step 0: Stripe */}
          {step === 0 && (
            <div>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💳</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>Connect Payment Processing</h2>
              <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                Link your bank account via Stripe to accept credit card payments directly from parents. Funds go straight to your account — you receive 100% of every payment.
              </p>
              <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: 16, marginBottom: 24 }}>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8, fontWeight: 600 }}>What you'll need:</div>
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 2 }}>
                  ✓ Your name and address<br/>
                  ✓ Last 4 of SSN (for identity verification)<br/>
                  ✓ Bank account number and routing number
                </div>
              </div>
              {stripeConnected ? (
                <div>
                  <div className="alert alert-success" style={{ marginBottom: 16 }}>✓ Stripe account connected! Card payments are live.</div>
                  <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setStep(1)}>
                    Continue →
                  </button>
                </div>
              ) : (
                <div>
                  <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
                    onClick={handleStripeConnect} disabled={connecting}>
                    {connecting ? 'Redirecting to Stripe…' : '🔗 Connect Bank Account via Stripe'}
                  </button>
                  <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', fontSize: 13 }}
                    onClick={() => setStep(1)}>
                    Skip for now — set up payments later
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Email */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>Configure Email Settings</h2>
              <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                Set up your from email address so reminders and welcome letters come from you, not a generic sender.
              </p>
              <div className="form-group">
                <label>From Email Address</label>
                <input type="email" value={emailForm.fromEmail}
                  onChange={e => setEmailForm(f => ({ ...f, fromEmail: e.target.value }))}
                  placeholder="yourname@email.com" autoFocus />
                <div className="helper-text">This must be verified in AWS SES. Check your email for a verification link after saving.</div>
              </div>
              <div className="form-group">
                <label>App URL</label>
                <input value={emailForm.frontendUrl}
                  onChange={e => setEmailForm(f => ({ ...f, frontendUrl: e.target.value }))} />
                <div className="helper-text">Used in links sent to parents. Default is correct if you're using app.duestrack.com.</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost" onClick={() => setStep(2)} style={{ flex: 1, justifyContent: 'center' }}>Skip</button>
                <button className="btn btn-primary" onClick={handleEmailSave} disabled={savingEmail} style={{ flex: 2, justifyContent: 'center' }}>
                  {savingEmail ? 'Saving…' : 'Save & Continue →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Create Project */}
          {step === 2 && (
            <div>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>Create Your First Project</h2>
              <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                A project represents a season, team, or fundraiser. You can have as many as you need. Each one has its own players, balances, and payment schedules.
              </p>
              <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: 16, marginBottom: 24 }}>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12, fontWeight: 600 }}>What to do next:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { icon: '📁', text: 'Create a project (e.g. "OL Red 2016 · 2026-27 Season")' },
                    { icon: '👤', text: 'Add your players with their starting balances and parent emails' },
                    { icon: '📋', text: 'Set up payment schedules — deposit + monthly installments' },
                    { icon: '✉️', text: 'Send welcome letters to all families in one click' },
                  ].map(item => (
                    <div key={item.text} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--text)' }}>
                      <span>{item.icon}</span><span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setStep(3)}>
                Go to Projects Dashboard →
              </button>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
              <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 12, color: 'var(--text)' }}>You're all set!</h2>
              <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>
                Your organization is ready. Head to the Projects dashboard to create your first project and start adding players.
              </p>
              <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: 16, marginBottom: 24, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>Quick tips:</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 2 }}>
                  💡 Use <strong>List View</strong> (user menu) to see all players in a table with inline actions<br/>
                  💡 Set up <strong>Auto-Remind</strong> on a project to send monthly reminders automatically<br/>
                  💡 The <strong>📥 Export CSV</strong> button downloads all transactions for a project<br/>
                  💡 Invite a <strong>co-manager</strong> from Team Members in the sidebar
                </div>
              </div>
              <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} onClick={onComplete}>
                Take Me to My Dashboard →
              </button>
            </div>
          )}
        </div>

        {/* Skip onboarding entirely */}
        {step < 3 && (
          <div style={{ textAlign: 'center' }}>
            <button onClick={onSkip} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13 }}>
              Skip setup — I'll configure this later in Settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
